class ZakMacsTheme {
  constructor() {
    this.toast = document.querySelector('[data-cart-toast]');
    this.cartStatus = document.querySelector('[data-cart-status]');
    this.bindMenu();
    this.bindMegaMenus();
    this.bindAjaxForms();
    this.bindCartQuantities();
    this.refreshCart();
  }

  bindMenu() {
    const buttons = [...document.querySelectorAll('[data-menu-toggle]')];
    const menu = document.querySelector('[data-mobile-nav]');
    if (!buttons.length || !menu) return;
    let activeButton = buttons[0];
    const close = () => {
      menu.removeAttribute('open');
      buttons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', 'Open menu');
      });
      document.body.classList.remove('menu-open');
    };
    buttons.forEach((button) => button.addEventListener('click', () => {
      activeButton = button;
      const open = !menu.hasAttribute('open');
      menu.toggleAttribute('open', open);
      buttons.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      });
      document.body.classList.toggle('menu-open', open);
    }));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.hasAttribute('open')) {
        close();
        activeButton.focus();
      }
    });
    document.addEventListener('click', (event) => {
      if (menu.hasAttribute('open') && !menu.contains(event.target) && !buttons.some((button) => button.contains(event.target))) close();
    });
  }

  bindMegaMenus() {
    const menus = [...document.querySelectorAll('.nav-mega')];
    if (!menus.length) return;
    menus.forEach((menu) => menu.addEventListener('toggle', () => {
      if (!menu.open) return;
      menus.forEach((other) => { if (other !== menu) other.removeAttribute('open'); });
    }));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openMenu = menus.find((menu) => menu.open);
      if (!openMenu) return;
      openMenu.removeAttribute('open');
      openMenu.querySelector('summary')?.focus();
    });
    document.addEventListener('click', (event) => {
      menus.forEach((menu) => { if (menu.open && !menu.contains(event.target)) menu.removeAttribute('open'); });
    });
  }

  bindAjaxForms(root = document) {
    root.querySelectorAll('[data-ajax-add]:not([data-bound])').forEach((form) => {
      form.dataset.bound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Unable to add that item.');
          await this.refreshCart();
          this.showAddFeedback(submit, data.product_title || 'Item');
          document.dispatchEvent(new CustomEvent('zm:item-added', { detail: { form, item: data } }));
          document.dispatchEvent(new CustomEvent('zm:cart-change'));
        } catch (error) {
          this.showToast(error.message);
        } finally {
          submit?.removeAttribute('aria-busy');
        }
      });
    });
  }

  bindCartQuantities() {
    const inputs = [...document.querySelectorAll('[data-cart-quantity]')];
    if (!inputs.length) return;
    const checkoutButtons = [...document.querySelectorAll('.cart-summary__actions button[type="submit"]')];
    let saveTimer;
    const updatePreview = () => {
      ['fudge', 'toffee'].forEach((type) => {
        const count = inputs.reduce((total, input) => input.dataset.promoQuantity === type
          ? total + Math.max(0, Number.parseInt(input.value, 10) || 0)
          : total, 0);
        this.updatePromoProgress(type, count);
      });
    };
    const save = async (input) => {
      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      const status = input.closest('.cart-item__quantity')?.querySelector('[data-quantity-status]');
      if (status) status.textContent = 'Saving...';
      inputs.forEach((field) => { field.disabled = true; });
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: input.dataset.cartKey, quantity })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || 'Unable to update the cart.');
        if (status) status.textContent = 'Updated';
        this.updateShippingPromo(cart.items_subtotal_price);
        window.setTimeout(() => window.location.reload(), 300);
      } catch (error) {
        if (status) status.textContent = 'Try again';
        inputs.forEach((field) => { field.disabled = false; });
        checkoutButtons.forEach((button) => { button.disabled = false; });
        this.showToast(error.message);
      }
    };
    inputs.forEach((input) => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
      input.addEventListener('input', () => {
        updatePreview();
        checkoutButtons.forEach((button) => { button.disabled = true; });
        window.clearTimeout(saveTimer);
        saveTimer = window.setTimeout(() => save(input), 650);
      });
    });
  }


  async refreshCart() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((node) => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;
      });
      ['fudge', 'toffee'].forEach((type) => {
        const count = cart.items.reduce((total, item) => {
          const itemType = item.properties && item.properties._zm_promo;
          return total + (itemType === type ? item.quantity : 0);
        }, 0);
        this.updatePromoProgress(type, count);
      });
      this.updateShippingPromo(cart.items_subtotal_price);
    } catch (_) {}
  }

  updatePromo(count) {
    const remainder = count % 4;
    let message = 'Buy 3 pieces of fudge and choose the 4th free.';
    if (count > 0 && remainder === 0) message = `${count / 4} free ${count === 4 ? 'piece is' : 'pieces are'} included. Start another set anytime.`;
    if (remainder === 1) message = 'Add 2 more pieces, then choose your free fourth.';
    if (remainder === 2) message = 'Add 1 more piece, then choose your free fourth.';
    if (remainder === 3) message = 'You earned a free piece—choose it before checkout!';
    document.querySelectorAll('[data-promo-progress]').forEach((node) => {
      node.style.setProperty('--progress', `${remainder === 0 && count ? 100 : (remainder / 4) * 100}%`);
      const text = node.querySelector('[data-promo-message]');
      if (text) text.textContent = message;
    });
  }

  showAddFeedback(button, productTitle) {
    const message = `${productTitle} added to your cart.`;
    if (this.cartStatus) {
      this.cartStatus.textContent = '';
      window.requestAnimationFrame(() => { this.cartStatus.textContent = message; });
    }
    if (!button) return;
    const iconButton = button.matches('.quick-add,.stage-product__add');
    const originalContent = button.dataset.originalContent || button.innerHTML;
    button.dataset.originalContent = originalContent;
    window.clearTimeout(button.addFeedbackTimer);
    button.querySelector('.add-feedback')?.remove();
    button.classList.remove('is-added');
    void button.offsetWidth;
    button.classList.add('is-added');
    button.textContent = iconButton ? '✓' : 'Added ✓';
    const feedback = document.createElement('span');
    feedback.className = 'add-feedback';
    feedback.setAttribute('aria-hidden', 'true');
    feedback.textContent = '+1';
    button.append(feedback);
    button.addFeedbackTimer = window.setTimeout(() => {
      button.classList.remove('is-added');
      button.innerHTML = originalContent;
    }, 1100);
  }

  updatePromoProgress(type, count) {
    const goal = type === 'toffee' ? 5 : 3;
    const paid = goal - 1;
    const ordinal = type === 'toffee' ? 'fifth' : 'third';
    const remainder = count % goal;
    const setCount = remainder === 0 && count ? goal : remainder;
    let message = 'Choose 3 pieces to unlock your free fourth.';
    if (remainder === 0 && count) message = `${count / 4} free ${count === 4 ? 'piece is' : 'pieces are'} in your cart. Start another set anytime.`;
    if (remainder === 1) message = 'Great start—choose 2 more, then add your free fourth.';
    if (remainder === 2) message = 'Choose 1 more piece to unlock your free fourth.';
    if (remainder === 3) message = 'Your next piece is free—choose it before checkout!';
    if (!count) message = `Choose ${paid} pieces to unlock your free ${ordinal}.`;
    if (remainder === 0 && count) message = `${count / goal} free ${count === goal ? 'piece is' : 'pieces are'} in your cart. Start another set anytime.`;
    if (remainder > 0 && remainder < paid) message = `Choose ${paid - remainder} more ${paid - remainder === 1 ? 'piece' : 'pieces'} to unlock your free ${ordinal}.`;
    if (remainder === paid) message = 'Your next piece is free—choose it before checkout!';
    document.querySelectorAll(`[data-promo-progress][data-promo-type="${type}"]`).forEach((node) => {
      node.style.setProperty('--progress', `${remainder === 0 && count ? 100 : (remainder / goal) * 100}%`);
      node.dataset.count = String(setCount);
      const text = node.querySelector('[data-promo-message]');
      if (text) text.textContent = message;
      const set = node.querySelector('[data-promo-set]');
      if (set) set.textContent = `${setCount} of ${goal} pieces`;
      node.querySelectorAll('[data-promo-piece]').forEach((piece) => {
        piece.classList.toggle('is-filled', Number(piece.dataset.promoPiece) <= setCount);
        piece.classList.toggle('is-free', Number(piece.dataset.promoPiece) === goal && setCount === goal);
      });
    });
  }

  updateShippingPromo(subtotal) {
    const amount = Math.max(0, Number(subtotal) || 0);
    document.querySelectorAll('[data-shipping-progress]').forEach((node) => {
      const goal = Math.max(1, Number(node.dataset.shippingGoal) || 10000);
      const unlocked = amount >= goal;
      const remaining = Math.max(0, goal - amount);
      const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(cents / 100);

      node.style.setProperty('--progress', `${Math.min(100, (amount / goal) * 100)}%`);
      node.classList.toggle('is-unlocked', unlocked);
      const total = node.querySelector('[data-shipping-total]');
      if (total) total.textContent = `${formatMoney(amount)} of ${formatMoney(goal)}`;
      const message = node.querySelector('[data-shipping-message]');
      if (message) message.textContent = unlocked
        ? 'You\u2019ve unlocked free Continental U.S. shipping!'
        : `You\u2019re ${formatMoney(remaining)} away from free Continental U.S. shipping.`;
      const track = node.querySelector('[data-shipping-track]');
      if (track) track.setAttribute('aria-valuenow', String(Math.min(amount, goal)));
    });
  }

  showToast(message) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { this.toast.hidden = true; }, 3200);
  }
}

window.zakMacsTheme = new ZakMacsTheme();
